import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { toast } from 'sonner'
import { getInitials } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SectionCard } from '@/components/dashboard/section-card'
import { EMAIL_PATTERN, FormField, fieldMessageId } from '@/components/dashboard/form-field'
import { CURRENT_USER } from '@/data/team'

type ProfileValues = {
    name: string
    email: string
    title: string
    phone: string
}

type ProfileErrors = Partial<Record<keyof ProfileValues, string>>

const INITIAL: ProfileValues = {
    name: CURRENT_USER.name,
    email: CURRENT_USER.email,
    title: CURRENT_USER.title,
    phone: CURRENT_USER.phone
}

const ORDER: (keyof ProfileValues)[] = ['name', 'email', 'title', 'phone']

const validate = (values: ProfileValues): ProfileErrors => {
    const errors: ProfileErrors = {}
    const digits = values.phone.replace(/\D/g, '')

    if (values.name.trim().length < 3) errors.name = 'Nama minimal 3 karakter.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Masukkan email yang valid.'
    if (!values.title.trim()) errors.title = 'Jabatan wajib diisi.'
    if (values.phone.trim() && (!/^[0-9+()\s-]+$/.test(values.phone) || digits.length < 9 || digits.length > 15)) {
        errors.phone = 'Nomor telepon terdiri dari 9 sampai 15 angka.'
    }

    return errors
}

export const ProfileForm: FC = (): ReactNode => {
    const [saved, setSaved] = useState<ProfileValues>(INITIAL)
    const [values, setValues] = useState<ProfileValues>(INITIAL)
    const [errors, setErrors] = useState<ProfileErrors>({})
    const dirty = ORDER.some((key) => values[key] !== saved[key])

    const update = (key: keyof ProfileValues, value: string): void => {
        setValues((current) => ({ ...current, [key]: value }))
        if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }))
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        const nextErrors = validate(values)
        setErrors(nextErrors)

        const firstInvalid = ORDER.find((key) => nextErrors[key])

        if (firstInvalid) {
            document.getElementById(`profile-${firstInvalid}`)?.focus()
            return
        }

        const trimmed = {
            name: values.name.trim(),
            email: values.email.trim().toLowerCase(),
            title: values.title.trim(),
            phone: values.phone.trim()
        }

        setValues(trimmed)
        setSaved(trimmed)
        toast.success('Profil disimpan', { description: 'Perubahan langsung terlihat oleh anggota tim lain.' })
    }

    const field = (key: keyof ProfileValues) => ({
        id: `profile-${key}`,
        value: values[key],
        onChange: (event: { target: { value: string } }) => update(key, event.target.value),
        'aria-invalid': Boolean(errors[key]),
        'aria-describedby': errors[key] ? fieldMessageId(`profile-${key}`) : undefined
    })

    return (
        <form onSubmit={onSubmit} noValidate>
            <SectionCard
                title='Profil'
                description='Nama dan kontak yang tampil di dasbor serta di email notifikasi.'
                footer={(
                    <div className='flex flex-wrap items-center justify-end gap-2'>
                        <Button
                            type='button'
                            variant='ghost'
                            disabled={!dirty}
                            onClick={() => {
                                setValues(saved)
                                setErrors({})
                            }}
                        >
                            Batalkan
                        </Button>
                        <Button type='submit' disabled={!dirty}>
                            Simpan perubahan
                        </Button>
                    </div>
                )}
            >
                <div className='flex items-center gap-4 pb-6'>
                    <span className='flex size-14 items-center justify-center rounded-full bg-foreground text-base font-semibold text-background' aria-hidden='true'>
                        {getInitials(values.name || saved.name)}
                    </span>
                    <div>
                        <p className='font-medium text-foreground'>{saved.name}</p>
                        <p className='text-sm text-muted-foreground'>Pemilik workspace Arunika</p>
                    </div>
                </div>

                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField id='profile-name' label='Nama lengkap' required error={errors.name}>
                        <Input {...field('name')} autoComplete='name' />
                    </FormField>

                    <FormField id='profile-email' label='Email' required error={errors.email}>
                        <Input {...field('email')} type='email' inputMode='email' autoComplete='email' />
                    </FormField>

                    <FormField id='profile-title' label='Jabatan' required error={errors.title}>
                        <Input {...field('title')} autoComplete='organization-title' />
                    </FormField>

                    <FormField id='profile-phone' label='Nomor telepon' error={errors.phone} hint='Dipakai untuk peringatan penting lewat WhatsApp.'>
                        <Input
                            {...field('phone')}
                            type='tel'
                            inputMode='tel'
                            autoComplete='tel'
                            aria-describedby={fieldMessageId('profile-phone')}
                        />
                    </FormField>
                </div>
            </SectionCard>
        </form>
    )
}
